'use client';
import { useState } from 'react';
import { Divider } from 'primereact/divider';
import { formatToIndianCurrency } from '@/_config';

/* ─────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────── */
const formatExpiry = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const couponSavingText = (c) => {
    if (c.discountType === 'percentage') {
        const base = `${c.discountValue}% off`;
        return c.maxDiscount ? `${base} (max ${formatToIndianCurrency(c.maxDiscount)})` : base;
    }
    return `Flat ${formatToIndianCurrency(c.discountValue)} off`;
};

/* ─────────────────────────────────────────────────────────────────
   CouponChip
───────────────────────────────────────────────────────────────── */
function CouponChip({ coupon }) {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard?.writeText(coupon.code).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
    };

    const expiry = formatExpiry(coupon.expiresAt);

    return (
        <div
            style={{
                border: '1.5px dashed #1bb8a7',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)',
                padding: '0.75rem 1rem 0.75rem 1.2rem',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* left stripe */}
            <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                background: 'linear-gradient(180deg, #1bb8a7, #1bb8a7)',
                borderRadius: '10px 0 0 10px',
            }} />

            {/* Code + copy button */}
            <div className="flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-tag" style={{ fontSize: '0.78rem', color: '#1bb8a7' }} />
                    <span style={{
                        fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem',
                        letterSpacing: '0.1em', color: '#0f766e',
                        background: '#ccfbf1', padding: '0.15rem 0.6rem',
                        borderRadius: '5px', border: '1px solid #99f6e4',
                    }}>
                        {coupon.code}
                    </span>
                </div>

                <button
                    onClick={copy}
                    style={{
                        background: copied ? '#dcfce7' : 'white',
                        border: `1.5px solid ${copied ? '#86efac' : '#1bb8a7'}`,
                        color: copied ? '#16a34a' : '#1bb8a7',
                        borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.72rem', fontWeight: 700,
                        padding: '0.2rem 0.65rem',
                        display: 'flex', alignItems: 'center', gap: '0.3rem',
                        transition: 'all 0.2s', minWidth: '5rem',
                        justifyContent: 'center',
                    }}
                >
                    <i className={`pi ${copied ? 'pi-check' : 'pi-copy'}`} style={{ fontSize: '0.68rem' }} />
                    {copied ? 'Copied!' : 'Copy Code'}
                </button>
            </div>

            {/* Saving label */}
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1bb8a7', marginBottom: '0.3rem' }}>
                {couponSavingText(coupon)}
            </div>

            {/* Description */}
            {coupon.description && (
                <div style={{ fontSize: '0.76rem', color: '#475569', marginBottom: '0.35rem' }}>
                    {coupon.description}
                </div>
            )}

            {/* Min order + expiry */}
            <div className="flex gap-3 flex-wrap">
                {coupon.minOrderValue > 0 && (
                    <span style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <i className="pi pi-shopping-cart" style={{ fontSize: '0.62rem' }} />
                        Min. order {formatToIndianCurrency(coupon.minOrderValue)}
                    </span>
                )}
                {expiry && (
                    <span style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <i className="pi pi-calendar" style={{ fontSize: '0.62rem' }} />
                        Valid till {expiry}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────
   Section header — reused across all three panels
───────────────────────────────────────────────────────────────── */
function SectionHeader({ iconBg, iconColor, icon, title, subtitle }) {
    return (
        <div className="flex align-items-center gap-2 mb-3">
            <div style={{
                width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0,
                background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <i className={`pi ${icon}`} style={{ color: iconColor, fontSize: '0.85rem' }} />
            </div>
            <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{title}</div>
                {subtitle && <div style={{ fontSize: '0.7rem'}}>{subtitle}</div>}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────
   OfferSidebarContent  (exported)
───────────────────────────────────────────────────────────────── */
export function OfferSidebarContent({ discountForAll = 0, prepaidDiscount = 0, coupons = [], allowCOD = true }) {
    return (
        <div className="flex flex-column gap-4" style={{ paddingBottom: '2rem' }}>

            {/* ── Coupon Offers ────────────────────────────────── */}
            {coupons.length > 0 && (
                <div>
                    <SectionHeader
                        icon="pi-ticket" iconBg="#ccfbf1" iconColor="#1bb8a7"
                        title="Coupon Offers"
                        subtitle={`${coupons.length} coupon${coupons.length > 1 ? 's' : ''} available`}
                    />
                    <div className="flex flex-column gap-3">
                        {coupons.map((c) => (
                            <CouponChip key={c._id ?? c.code} coupon={c} />
                        ))}
                    </div>
                </div>
            )}

            {coupons.length > 0 && (discountForAll > 0 || prepaidDiscount > 0) && (
                <Divider style={{ margin: '0' }} />
            )}

            {/* ── Bank & Promo Offers ───────────────────────────── */}
            {(discountForAll > 0 || prepaidDiscount > 0) && (
                <div>
                    <SectionHeader
                        icon="pi-percentage" iconBg="#dbeafe" iconColor="#3b82f6"
                        title="Bank & Promo Offers"
                        subtitle="Auto-applied at checkout"
                    />

                    <div className="flex flex-column gap-2">
                        {discountForAll > 0 && (
                            <div style={{
                                borderRadius: '10px', border: '1px solid #bbf7d0',
                                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                padding: '0.75rem 1rem',
                                display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                            }}>
                                <div style={{
                                    width: '2.2rem', height: '2.2rem', borderRadius: '50%', flexShrink: 0,
                                    background: '#dcfce7', border: '1px solid #86efac',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-star-fill" style={{ fontSize: '0.82rem', color: '#16a34a' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#166534' }}>
                                        Discount for you
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#15803d', fontWeight: 600, margin: '0.15rem 0' }}>
                                        Save {formatToIndianCurrency(discountForAll)}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: '#4b5563' }}>
                                        Automatically applied on every order at checkout — no code needed.
                                    </div>
                                </div>
                            </div>
                        )}

                        {prepaidDiscount > 0 && (
                            <div style={{
                                borderRadius: '10px', border: '1px solid #bfdbfe',
                                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                padding: '0.75rem 1rem',
                                display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                            }}>
                                <div style={{
                                    width: '2.2rem', height: '2.2rem', borderRadius: '50%', flexShrink: 0,
                                    background: '#dbeafe', border: '1px solid #93c5fd',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-credit-card" style={{ fontSize: '0.82rem', color: '#3b82f6' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e40af' }}>
                                        Pay Online &amp; Save
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#1d4ed8', fontWeight: 600, margin: '0.15rem 0' }}>
                                        Extra {formatToIndianCurrency(prepaidDiscount)} off
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: '#4b5563' }}>
                                        Pay via UPI, Debit / Credit Card, or Net Banking to unlock this discount.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <Divider style={{ margin: '0' }} />

            {/* ── Payment Information ───────────────────────────── */}
            <div>
                <SectionHeader
                    icon="pi-lock" iconBg="#faf5ff" iconColor="#8b5cf6"
                    title="Payment Information"
                    subtitle="Safe &amp; secure checkout"
                />

                <div className="flex flex-column gap-2">
                    {/* Accepted modes */}
                    <div style={{
                        borderRadius: '8px', background: '#f8fafc',
                        border: '1px solid #e2e8f0', padding: '0.7rem 0.9rem',
                    }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                            Accepted Payment Modes
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {[
                                { icon: 'pi-mobile',      label: 'UPI',         color: '#1bb8a7', bg: '#ccfbf1' },
                                { icon: 'pi-credit-card', label: 'Credit Card', color: '#3b82f6', bg: '#dbeafe' },
                                { icon: 'pi-wallet',      label: 'Debit Card',  color: '#8b5cf6', bg: '#ede9fe' },
                                { icon: 'pi-building',    label: 'Net Banking', color: '#f59e0b', bg: '#fef3c7' },
                                ...(allowCOD ? [{ icon: 'pi-money-bill', label: 'COD', color: '#16a34a', bg: '#dcfce7' }] : []),
                            ].map(({ icon, label, color, bg }) => (
                                <div key={label} style={{
                                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                                    background: bg, color,
                                    border: `1px solid ${bg}`, borderRadius: '6px',
                                    padding: '0.2rem 0.55rem', fontSize: '0.7rem', fontWeight: 600,
                                }}>
                                    <i className={`pi ${icon}`} style={{ fontSize: '0.65rem' }} />
                                    {label}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Prepaid tip */}
                    {prepaidDiscount > 0 && (
                        <div style={{
                            borderRadius: '8px', background: '#fffbeb',
                            border: '1px solid #fde68a', padding: '0.6rem 0.9rem',
                            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                        }}>
                            <i className="pi pi-info-circle" style={{ color: '#d97706', fontSize: '0.8rem', marginTop: '1px' }} />
                            <span style={{ fontSize: '0.72rem', color: '#92400e', lineHeight: '1.5' }}>
                                <strong>Tip:</strong> Pay online (UPI / Card) to save an additional{' '}
                                <strong>{formatToIndianCurrency(prepaidDiscount)}</strong> on your order.
                                {allowCOD && ' COD orders do not qualify for the prepaid discount.'}
                            </span>
                        </div>
                    )}

                    {/* Secure note */}
                    <div style={{
                        borderRadius: '8px', background: '#f1f5f9',
                        border: '1px solid #e2e8f0', padding: '0.6rem 0.9rem',
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                        <i className="pi pi-shield" style={{ color: '#1bb8a7', fontSize: '0.9rem' }} />
                        <span style={{ fontSize: '0.72rem', color: '#475569' }}>
                            All transactions are secured and encrypted via PayU payment gateway.
                        </span>
                    </div>
                </div>
            </div>

        </div>
    );
}
